"use client";

/**
 * THE PALETTE AXIS — which colours the app wears, independent of face and density.
 *
 * WHAT REPLACED WHAT. This module replaces `hue.ts` and the four
 * `[data-theme]` blocks that used to live in globals.css. Those blocks were
 * hand-authored ramps — twelve `oklch()` literals each, chroma chosen by eye —
 * and they had three problems that no amount of care would fix: a fifth theme
 * was a code change, the values could be (and were) outside the sRGB gamut
 * without anyone noticing, and nothing about them could be edited at runtime.
 *
 * A palette is now DATA: two hues plus twelve chroma values. Applying one is
 * writing fourteen custom properties onto <html>. globals.css derives the
 * entire ramp, every semantic token, both faces, the shadows, the gradients
 * and the charts from those fourteen numbers — so a swap needs no stylesheet
 * and no deploy, and an admin surface can author a palette that has never
 * existed in the repo.
 *
 * The chroma values are never typed by a human. `oklch.ts::rampFor` computes
 * them against the real sRGB boundary, which is why a built-in and a
 * user-authored palette are the same kind of thing and go through the same
 * validation.
 *
 * CONSTRUCTION mirrors `density.ts` and `auto-theme.ts` exactly — a boot
 * script that stamps before first paint (no flash), a `useSyncExternalStore`
 * mirror, one storage key, cross-tab follow via the `storage` event. The
 * properties go on <html>, never a wrapper, so portalled Radix surfaces
 * inherit them through the cascade.
 */

import { useSyncExternalStore } from "react";
import { LADDER, rampFor, type Ramp } from "./oklch";

export const PALETTE_STORAGE_KEY = "hh.palette";

/** A palette as it travels: on the wire, in localStorage, in a database row. */
export interface Palette {
  slug: string;
  name: string;
  hueAccent: number;
  hueNeutral: number;
  /** Zero gives genuinely neutral surfaces; colored palettes default to one. */
  neutralChroma?: number;
  /** Twelve values, index-aligned to `LADDER`. Derived, never authored. */
  chroma: number[];
  /** 1-based ramp stops each face uses as `--primary`. */
  darkStop: number;
  lightStop: number;
  /**
   * Status hues are fixed so green reads green in every palette. The one
   * sanctioned override is `danger` for an accent that lands near hue 25 —
   * without it, "save" and "delete" would be the same colour.
   */
  statusDangerHue?: number;
}

/** Build a complete palette from the two decisions a human actually makes. */
export function definePalette(
  slug: string,
  name: string,
  opts: {
    hueAccent: number;
    hueNeutral?: number;
    statusDangerHue?: number;
  },
): Palette {
  const { hueAccent } = opts;
  const ramp: Ramp = rampFor(hueAccent);
  return {
    slug,
    name,
    hueAccent,
    hueNeutral: opts.hueNeutral ?? hueAccent,
    chroma: ramp.chroma,
    darkStop: ramp.darkPrimary + 1,
    lightStop: ramp.lightPrimary + 1,
    statusDangerHue: opts.statusDangerHue,
  };
}

/**
 * The built-ins.
 *
 * Cerulean is the default. It peaks at L 0.77 with chroma 0.151 — blues carry
 * their colour high, which a ranking taken at one fixed lightness badly
 * under-states — and it reaches 9.4:1 against the dark ground, the highest of
 * any candidate measured.
 *
 * A palette is ONE hue. There used to be a `huePartner` — the violet the brand
 * gradient ran towards — and it was the single place the app went rainbow.
 * The gradient is tonal now (globals.css), so a palette needs no second hue.
 *
 * Oxblood carries the danger override: its accent sits at hue 18, seven
 * degrees from the global danger hue, so danger moves to a cool crimson.
 */
export const BUILT_IN_PALETTES: Palette[] = [
  { slug: "graphite", name: "Graphite", hueAccent: 0, hueNeutral: 0,
    chroma: Array(12).fill(0), darkStop: 2, lightStop: 12, neutralChroma: 0 },
  definePalette("cerulean", "Cerulean", { hueAccent: 228, hueNeutral: 235 }),
  definePalette("violet", "Violet", { hueAccent: 295, hueNeutral: 285 }),
  definePalette("azure", "Azure", { hueAccent: 245, hueNeutral: 250 }),
  definePalette("emerald", "Emerald", { hueAccent: 155, hueNeutral: 175 }),
  definePalette("oxblood", "Oxblood", {
    hueAccent: 18,
    hueNeutral: 25,
    statusDangerHue: 350,
  }),
];

export const DEFAULT_PALETTE_SLUG = "graphite";
const DEFAULT_MIGRATION_KEY = "hh.palette.neutral-default.v1";

export function paletteBySlug(slug: string): Palette | undefined {
  return BUILT_IN_PALETTES.find((p) => p.slug === slug);
}

/**
 * A palette as the CSS custom properties that carry it: two hues, twelve
 * chroma values, the two primary stops, and (rarely) a danger-hue override.
 *
 * This function is THE contract between the data and the stylesheet. It is
 * shared by the boot script, the runtime setter and the preview surface, so a
 * swatch in the admin form is painted by exactly the mechanism that paints the
 * app — there is no second code path that could disagree.
 */
export function paletteVars(p: Palette): Record<string, string> {
  const vars: Record<string, string> = {
    "--hue-accent": String(p.hueAccent),
    "--hue-neutral": String(p.hueNeutral),
    "--neutral-chroma": String(p.neutralChroma ?? 1),
    "--primary-dark-stop": `var(--accent-${p.darkStop})`,
    "--primary-light-stop": `var(--accent-${p.lightStop})`,
  };
  p.chroma.forEach((c, i) => {
    vars[`--c${i + 1}`] = c.toFixed(3);
  });
  if (p.statusDangerHue !== undefined) {
    vars["--status-danger-h"] = String(p.statusDangerHue);
  }
  return vars;
}

/** Apply a palette to an element — <html> in the app, a preview box in admin. */
export function applyPalette(el: HTMLElement, p: Palette): void {
  const vars = paletteVars(p);
  for (const [k, v] of Object.entries(vars)) el.style.setProperty(k, v);
  // A palette without the override must CLEAR it, or a previous palette's
  // danger hue survives the swap — the bug that a plain "set" would ship.
  if (p.statusDangerHue === undefined) el.style.removeProperty("--status-danger-h");
  el.dataset.palette = p.slug;
}

/**
 * Inlined into <head> BEFORE hydration. Kept as a string with the palettes
 * baked in so it ships with zero module dependencies and cannot disagree with
 * the constants above. An unknown stored slug falls back to the default rather
 * than to an unstyled document.
 */
export const PALETTE_BOOT_SCRIPT = `(function(){try{
var P=${JSON.stringify(
  Object.fromEntries(BUILT_IN_PALETTES.map((p) => [p.slug, paletteVars(p)])),
)};
var s=localStorage.getItem(${JSON.stringify(PALETTE_STORAGE_KEY)});
if(!localStorage.getItem(${JSON.stringify(DEFAULT_MIGRATION_KEY)})){
if(!s||s==="cerulean"){s=${JSON.stringify(DEFAULT_PALETTE_SLUG)};localStorage.setItem(${JSON.stringify(PALETTE_STORAGE_KEY)},s)}
localStorage.setItem(${JSON.stringify(DEFAULT_MIGRATION_KEY)},"1");
}
var v=P[s]||P[${JSON.stringify(DEFAULT_PALETTE_SLUG)}];
var d=document.documentElement;
for(var k in v){d.style.setProperty(k,v[k])}
if(!v["--status-danger-h"]){d.style.removeProperty("--status-danger-h")}
d.dataset.palette=P[s]?s:${JSON.stringify(DEFAULT_PALETTE_SLUG)};
}catch(e){}})()`;

const listeners = new Set<() => void>();

function read(): string {
  if (typeof window === "undefined") return DEFAULT_PALETTE_SLUG;
  try {
    const stored = window.localStorage.getItem(PALETTE_STORAGE_KEY);
    return stored && paletteBySlug(stored) ? stored : DEFAULT_PALETTE_SLUG;
  } catch {
    return DEFAULT_PALETTE_SLUG;
  }
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  const onStorage = (e: StorageEvent) => {
    if (e.key === PALETTE_STORAGE_KEY) fn();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(fn);
    window.removeEventListener("storage", onStorage);
  };
}

export function setPalette(slug: string): void {
  const p = paletteBySlug(slug);
  if (!p) return;
  try {
    window.localStorage.setItem(PALETTE_STORAGE_KEY, slug);
  } catch {
    /* private mode — the swap still applies for this session */
  }
  applyPalette(document.documentElement, p);
  listeners.forEach((fn) => fn());
}

export function usePalette(): string {
  return useSyncExternalStore(subscribe, read, () => DEFAULT_PALETTE_SLUG);
}

/** The ladder, re-exported so a preview can render stops without importing the engine. */
export { LADDER };
